import { useEffect, useState } from 'react';
import { useAuth } from '@/modules/auth/useAuth';
import { getSupabase } from '@/lib/supabase';
import { subscribeTable } from '@/lib/realtime';
import {
  deriveVerificationStatus,
  type VerificationStatus,
} from './verificationStatus';

export type { VerificationStatus } from './verificationStatus';
export { isVerified, canActOnGatedSurface } from './verificationStatus';

export interface VerificationState {
  status: VerificationStatus;
  loading: boolean;
}

/**
 * Reads the user's own row from `hottr_verifications` and subscribes to
 * realtime changes so the page updates the instant the Persona webhook
 * lands. Falls through to 'unverified' when no row exists yet.
 */
export const useVerification = (): VerificationState => {
  const { user } = useAuth();
  const [state, setState] = useState<VerificationState>({
    status: 'unverified',
    loading: true,
  });

  useEffect(() => {
    if (!user) {
      setState({ status: 'unverified', loading: false });
      return;
    }
    const supabase = getSupabase();
    if (!supabase) {
      setState({ status: 'unverified', loading: false });
      return;
    }

    let cancelled = false;
    const load = async () => {
      const { data } = await supabase
        .from('hottr_verifications')
        .select('status, expires_at')
        .eq('user_id', user.id)
        .maybeSingle();
      if (cancelled) return;
      const row = data as { status?: string; expires_at?: string | null } | null;
      const status = deriveVerificationStatus(
        row ? { status: row.status, expires_at: row.expires_at ?? null } : null,
        Date.now(),
      );
      setState({ status, loading: false });
    };

    load();

    const unsubscribe = subscribeTable(
      supabase,
      `hottr-verifications-${user.id}`,
      {
        event: '*',
        table: 'hottr_verifications',
        filter: `user_id=eq.${user.id}`,
      },
      load,
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [user?.id]);

  return state;
};
